import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "../../lib/utils";
import { Check, Minus, AlertTriangle } from "lucide-react";

const BASE_CLASSES = "h-4 w-4 rounded border flex items-center justify-center transition-all duration-200";

const getDisabledClasses = (isSelected) => {
  const selectedClass = isSelected ? "bg-gray-300" : "";
  return cn(BASE_CLASSES, "border-gray-300 bg-gray-100 cursor-not-allowed", selectedClass);
};

const getErrorClasses = (isSelected, focused) => {
  const selectedClass = isSelected ? "bg-red-500 border-red-500" : "";
  const focusClass = focused ? "ring-2 ring-red-200 ring-offset-2" : "";
  return cn(
    BASE_CLASSES,
    "border-red-500 hover:border-red-500 focus:border-red-500 bg-white",
    selectedClass,
    focusClass
  );
};

const getFocusedClasses = (isSelected, variant) => {
  const isSecondary = variant === "secondary";
  const focusClasses = isSecondary
    ? "border-purple-500 ring-2 ring-purple-200 ring-offset-2"
    : "border-secondary-2 ring-2 ring-secondary-2/20 ring-offset-2";
  const bgClass = getBgClassForFocused(isSelected, isSecondary);
  return cn(BASE_CLASSES, focusClasses, bgClass);
};

const getBgClassForFocused = (isSelected, isSecondary) => {
  if (!isSelected) return "bg-white";
  return isSecondary ? "bg-purple-500" : "bg-secondary-2";
};

const getCheckedClasses = (variant) => {
  const checkedClasses = variant === "secondary"
    ? "bg-purple-500 border-purple-500"
    : "bg-secondary-2 border-secondary-2";
  return cn(BASE_CLASSES, checkedClasses);
};

const getDefaultClasses = () => {
  return cn(BASE_CLASSES, "border-gray-400 bg-white hover:border-gray-500");
};

const getVariantClasses = ({ disabled, error, focused, checked, indeterminate, variant }) => {
  const isSelected = checked || indeterminate;

  if (disabled) return getDisabledClasses(isSelected);
  if (error) return getErrorClasses(isSelected, focused);
  if (focused) return getFocusedClasses(isSelected, variant);
  if (isSelected) return getCheckedClasses(variant);
  return getDefaultClasses();
};

const Checkbox = React.forwardRef(
  (
    {
      className,
      checked,
      indeterminate,
      disabled,
      error,
      focused,
      variant = "default",
      label,
      labelClassName,
      checkboxClasses,
      errorMessage,
      ...props
    },
    ref
  ) => {

    const getIconColor = () => {
      if (disabled) return "text-gray-500";
      return "text-white";
    };

    const variantClasses = getVariantClasses({ disabled, error, focused, checked, indeterminate, variant });

    return (
      <div className={cn("flex flex-col space-y-1", className)}>
        <div className="flex items-center space-x-3">
          <CheckboxPrimitive.Root
            ref={ref}
            className={cn(variantClasses, checkboxClasses)}
            checked={indeterminate ? "indeterminate" : checked}
            disabled={disabled}
            aria-invalid={error}
            aria-describedby={
              error && errorMessage ? `${props.id}-error` : undefined
            }
            {...props}
          >
            <CheckboxPrimitive.Indicator className="flex items-center justify-center">
              {indeterminate ? (
                <Minus className={cn("h-3 w-3", getIconColor())} />
              ) : (
                <Check className={cn("h-3 w-3", getIconColor())} />
              )}
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>

          {label && (
            <label
              htmlFor={props.id}
              className={cn(
                "text-sm font-medium leading-none peer-disabled:cursor-not-allowed",
                disabled ? "text-gray-400" : "text-gray-900",
                labelClassName || ""
              )}
            >
              {label}
            </label>
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
